import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  UserPlus,
  BadgeDollarSign,
  Sparkles,
  Building2,
  ShieldCheck,
  Search,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const fmt = (v) => (v === null || v === undefined || v === "" ? "—" : v);

/* ------------------------------------------------------------------ */
/* Tabs definition                                                    */
/* ------------------------------------------------------------------ */
const TABS = [
  { key: "sign_ups", label: "Sign-ups", icon: UserPlus, desc: "Free tier · no active membership" },
  { key: "paying", label: "Paying Members", icon: BadgeDollarSign, desc: "Access · Joiner · Crown · Platinum" },
  { key: "jewels", label: "Jewels", icon: Sparkles, desc: "JT Jewels itinerary bookings" },
  { key: "hosts", label: "Hosts", icon: Building2, desc: "Approved & applying hosts" },
  { key: "all", label: "All Members", icon: Users, desc: "Every user in the system" },
];

function TabButton({ tab, activeKey, count, onClick }) {
  const Icon = tab.icon;
  const isActive = activeKey === tab.key;
  return (
    <button
      type="button"
      onClick={() => onClick(tab.key)}
      data-testid={`admin-users-tab-${tab.key}`}
      className={`flex items-start gap-3 text-left border transition-colors px-4 py-3 min-w-[160px] ${
        isActive
          ? "border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--emerald)]"
          : "border-[var(--emerald)]/15 text-[var(--ink-2)] hover:border-[var(--gold)]/60"
      }`}
    >
      <Icon size={18} strokeWidth={1.25} className={isActive ? "text-[var(--gold)]" : "text-[var(--emerald)]"} />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em]">{tab.label}</div>
        <div className="font-display text-2xl tabular-nums text-[var(--emerald)] leading-tight mt-0.5">
          {count ?? "—"}
        </div>
        <div className="text-[10px] text-[var(--muted)] mt-0.5">{tab.desc}</div>
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Users table                                                        */
/* ------------------------------------------------------------------ */
function UsersTable({ users, showTier = true }) {
  if (!users || users.length === 0) {
    return (
      <div className="lux-card text-center text-sm text-[var(--muted)]" data-testid="admin-users-empty">
        No records match this segment.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm" data-testid="admin-users-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--emerald)]/10">
            <th className="text-left px-3 py-2 font-normal">Name</th>
            <th className="text-left px-3 py-2 font-normal">Email</th>
            <th className="text-left px-3 py-2 font-normal">Phone</th>
            <th className="text-left px-3 py-2 font-normal">Role</th>
            {showTier && <th className="text-left px-3 py-2 font-normal">Tier</th>}
            <th className="text-left px-3 py-2 font-normal">Member #</th>
            <th className="text-left px-3 py-2 font-normal">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              className="border-b border-[var(--emerald)]/5 hover:bg-[var(--ivory-2)]/60"
              data-testid={`admin-user-row-${u.id}`}
            >
              <td className="px-3 py-3 align-top text-[var(--emerald)]">{fmt(u.name)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink)]">{fmt(u.email)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink)]">{fmt(u.phone)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink-2)] capitalize">{fmt(u.role)}</td>
              {showTier && (
                <td className="px-3 py-3 align-top text-[var(--ink-2)] capitalize">
                  {u.membership_tier || "free"}
                </td>
              )}
              <td className="px-3 py-3 align-top text-[var(--gold)] tabular-nums">
                {fmt(u.membership_number)}
              </td>
              <td className="px-3 py-3 align-top text-[var(--muted)]">{fmtDate(u.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Jewels table (booking_requests)                                    */
/* ------------------------------------------------------------------ */
function JewelsTable({ jewels }) {
  if (!jewels || jewels.length === 0) {
    return (
      <div className="lux-card text-center text-sm text-[var(--muted)]" data-testid="admin-jewels-empty">
        No JT Jewel bookings yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm" data-testid="admin-jewels-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] border-b border-[var(--emerald)]/10">
            <th className="text-left px-3 py-2 font-normal">Trip ref</th>
            <th className="text-left px-3 py-2 font-normal">Guest</th>
            <th className="text-left px-3 py-2 font-normal">Email</th>
            <th className="text-left px-3 py-2 font-normal">Phone</th>
            <th className="text-left px-3 py-2 font-normal">Estimate</th>
            <th className="text-left px-3 py-2 font-normal">Status</th>
            <th className="text-left px-3 py-2 font-normal">Booked</th>
          </tr>
        </thead>
        <tbody>
          {jewels.map((j) => (
            <tr
              key={j.id}
              className="border-b border-[var(--emerald)]/5 hover:bg-[var(--ivory-2)]/60"
              data-testid={`admin-jewel-row-${j.id}`}
            >
              <td className="px-3 py-3 align-top text-[var(--gold)] font-mono">{fmt(j.recall_code)}</td>
              <td className="px-3 py-3 align-top text-[var(--emerald)]">{fmt(j.name)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink)]">{fmt(j.email)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink)]">{fmt(j.phone)}</td>
              <td className="px-3 py-3 align-top text-[var(--ink-2)] tabular-nums">
                {j.estimated_total ? `$${Number(j.estimated_total).toLocaleString()}` : "—"}
              </td>
              <td className="px-3 py-3 align-top text-[var(--ink-2)] capitalize">{fmt(j.status)}</td>
              <td className="px-3 py-3 align-top text-[var(--muted)]">{fmtDate(j.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */
export default function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(params.get("tab") || "sign_ups");
  const [tierFilter, setTierFilter] = useState(params.get("tier") || "");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Auth guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login?next=/admin/users");
      return;
    }
    if (user.role !== "admin") {
      setErr("Admin access required.");
    }
  }, [user, authLoading, navigate]);

  // Fetch summary counts
  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/admin/users/segmented");
      setSummary(r.data);
    } catch (e) {
      console.error("segmented summary failed", e);
    }
  }, []);

  // Fetch rows for active tab
  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "jewels") {
        const r = await api.get("/admin/jewels/list", { params: { q: q || undefined, limit: 200 } });
        setRows(r.data.jewels || []);
      } else {
        const r = await api.get("/admin/users/list", {
          params: {
            segment: activeTab,
            tier: activeTab === "paying" && tierFilter ? tierFilter : undefined,
            q: q || undefined,
            limit: 200,
          },
        });
        setRows(r.data.users || []);
      }
    } catch (e) {
      console.error("list failed", e);
      toast.error(e?.response?.data?.detail || "Could not load records.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, tierFilter, q]);

  useEffect(() => {
    if (user && user.role === "admin") loadSummary();
  }, [user, loadSummary]);

  useEffect(() => {
    if (user && user.role === "admin") loadRows();
  }, [user, loadRows]);

  // Sync tab -> URL
  const setTab = (key) => {
    setActiveTab(key);
    setTierFilter("");
    const next = new URLSearchParams();
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  const handleExport = () => {
    const slug = activeTab === "jewels" ? "booking_requests" : "users";
    const url = `${API}/admin/export/${slug}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("sasha_token")}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        return r.blob();
      })
      .then((blob) => {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        const filename = `jante-${slug}-${today}.csv`;
        const link = document.createElement("a");
        const obj = window.URL.createObjectURL(blob);
        link.href = obj;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(obj);
        toast.success(`${slug} exported.`);
      })
      .catch((e) => toast.error(e.message || "Export failed."));
  };

  const counts = summary?.counts || {};
  const tabCounts = useMemo(
    () => ({
      sign_ups: counts.sign_ups,
      paying: counts.paying,
      jewels: counts.jewels,
      hosts: counts.hosts,
      all: counts.total,
    }),
    [counts],
  );

  if (err) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="admin-users-forbidden">
        <ShieldCheck size={32} strokeWidth={1.25} className="text-[var(--burgundy)] mx-auto" />
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">Admins only.</h1>
        <p className="text-[var(--ink-2)] mt-4">{err}</p>
        <button onClick={() => navigate("/")} className="btn-primary mt-6">
          Back to Sasha
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14" data-testid="admin-users-page">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] hover:text-[var(--emerald)]"
        data-testid="admin-users-back-link"
      >
        <ArrowLeft size={12} strokeWidth={1.5} /> Back to dashboard
      </Link>

      <div className="mt-4 overline">Janté HQ · Members & bookings</div>
      <h1 className="font-display text-5xl text-[var(--emerald)] mt-2">Members deep-dive</h1>
      <p className="text-[var(--ink-2)] mt-3 max-w-xl text-sm">
        Segment sign-ups, paying members, hosts, and JT Jewels bookings — filter, search,
        and export in one place.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-3" data-testid="admin-users-tabs">
        {TABS.map((t) => (
          <TabButton
            key={t.key}
            tab={t}
            activeKey={activeTab}
            count={tabCounts[t.key]}
            onClick={setTab}
          />
        ))}
      </div>

      {/* Tier chips — only visible on paying tab */}
      {activeTab === "paying" && summary?.paying_by_tier && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-users-tier-chips">
          <button
            type="button"
            onClick={() => setTierFilter("")}
            data-testid="tier-chip-all"
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
              !tierFilter
                ? "bg-[var(--emerald)] text-[var(--ivory)] border-[var(--emerald)]"
                : "border-[var(--emerald)]/25 text-[var(--ink-2)] hover:border-[var(--gold)]"
            }`}
          >
            All paying · {summary.counts.paying}
          </button>
          {summary.paying_by_tier.map((t) => (
            <button
              key={t.tier}
              type="button"
              onClick={() => setTierFilter(t.tier)}
              data-testid={`tier-chip-${t.tier}`}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                tierFilter === t.tier
                  ? "bg-[var(--gold)] text-[var(--emerald)] border-[var(--gold)]"
                  : "border-[var(--emerald)]/25 text-[var(--ink-2)] hover:border-[var(--gold)]"
              }`}
            >
              {t.name} · {t.count}
            </button>
          ))}
        </div>
      )}

      {/* Search + export */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 border border-[var(--emerald)]/20 px-3 py-2 flex-1 min-w-[240px] max-w-md">
          <Search size={14} strokeWidth={1.5} className="text-[var(--muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="bg-transparent outline-none text-sm w-full text-[var(--emerald)]"
            data-testid="admin-users-search"
          />
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-colors px-3 py-2"
          data-testid="admin-users-export-btn"
        >
          <Download size={12} strokeWidth={1.5} /> Export CSV
        </button>
      </div>

      {/* Results */}
      <div className="mt-8" data-testid="admin-users-results">
        {loading ? (
          <div className="lux-card text-center text-sm text-[var(--muted)]">Loading…</div>
        ) : activeTab === "jewels" ? (
          <JewelsTable jewels={rows} />
        ) : (
          <UsersTable users={rows} showTier={activeTab !== "hosts" && activeTab !== "admins"} />
        )}
      </div>

      <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        Showing {rows.length} record{rows.length === 1 ? "" : "s"}.
      </div>
    </div>
  );
}
